import { Component, EventEmitter, Input, Output, OnInit, HostListener, ElementRef } from '@angular/core';

export interface DateRange {
  from: string | null;
  to: string | null;
}

@Component({
  selector: 'app-date-range-picker',
  standalone: false,
  templateUrl: './date-range-picker.html',
  styleUrls: ['./date-range-picker.css']
})
export class DateRangePickerComponent implements OnInit {
  @Input() fromDate: string | null = null;
  @Input() toDate: string | null = null;
  @Input() label: string = 'Date Range';
  @Input() placeholder: string = 'Select dates';
  @Input() floating: boolean = true;

  @Output() fromDateChange = new EventEmitter<string | null>();
  @Output() toDateChange = new EventEmitter<string | null>();
  @Output() rangeChange = new EventEmitter<DateRange>();

  isOpen: boolean = false;
  currentMonth: Date = new Date();
  calendarDays: (Date | null)[][] = [];
  weekDays: string[] = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  // Selection state
  selectionStart: Date | null = null;
  selectionEnd: Date | null = null;
  hoverDate: Date | null = null;

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.generateCalendar();
    if (this.fromDate) {
      this.selectionStart = new Date(this.fromDate);
      this.currentMonth = new Date(this.fromDate);
    }
    if (this.toDate) {
      this.selectionEnd = new Date(this.toDate);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  toggleCalendar() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.generateCalendar();
    }
  }

  generateCalendar() {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days: (Date | null)[][] = [];
    let week: (Date | null)[] = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startDay; i++) {
      week.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      week.push(new Date(year, month, day));
      if (week.length === 7) {
        days.push(week);
        week = [];
      }
    }

    // Fill remaining days in last week
    if (week.length > 0) {
      while (week.length < 7) {
        week.push(null);
      }
      days.push(week);
    }

    this.calendarDays = days;
  }

  prevMonth() {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
    this.generateCalendar();
  }

  onDateClick(date: Date) {
    if (!this.selectionStart || (this.selectionStart && this.selectionEnd)) {
      // Start new selection
      this.selectionStart = date;
      this.selectionEnd = null;
    } else {
      // Complete selection
      if (date < this.selectionStart) {
        this.selectionEnd = this.selectionStart;
        this.selectionStart = date;
      } else {
        this.selectionEnd = date;
      }
      this.emitValues();
    }
  }

  onDateHover(date: Date | null) {
    this.hoverDate = date;
  }

  isSelected(date: Date): boolean {
    if (!date) return false;
    const dateStr = this.formatDateISO(date);
    const startStr = this.selectionStart ? this.formatDateISO(this.selectionStart) : null;
    const endStr = this.selectionEnd ? this.formatDateISO(this.selectionEnd) : null;
    return dateStr === startStr || dateStr === endStr;
  }

  isInRange(date: Date): boolean {
    if (!date || !this.selectionStart) return false;

    const end = this.selectionEnd || this.hoverDate;
    if (!end) return false;

    let start = this.selectionStart;
    let endDate = end;

    if (end < start) {
      start = end;
      endDate = this.selectionStart;
    }

    return date > start && date < endDate;
  }

  isRangeStart(date: Date): boolean {
    if (!date || !this.selectionStart) return false;
    return this.formatDateISO(date) === this.formatDateISO(this.selectionStart);
  }

  isRangeEnd(date: Date): boolean {
    if (!date) return false;
    const end = this.selectionEnd || (this.hoverDate && this.selectionStart ? this.hoverDate : null);
    if (!end) return false;
    return this.formatDateISO(date) === this.formatDateISO(end);
  }

  isToday(date: Date): boolean {
    if (!date) return false;
    const today = new Date();
    return this.formatDateISO(date) === this.formatDateISO(today);
  }

  formatDateISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDisplayDate(date: Date | null): string {
    if (!date) return '';
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
  }

  get displayValue(): string {
    if (!this.selectionStart && !this.selectionEnd) {
      return '';
    }
    if (this.selectionStart && !this.selectionEnd) {
      return this.formatDisplayDate(this.selectionStart) + ' - ...';
    }
    return `${this.formatDisplayDate(this.selectionStart)} - ${this.formatDisplayDate(this.selectionEnd)}`;
  }

  get monthYearDisplay(): string {
    return this.currentMonth.toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' });
  }

  emitValues() {
    const from = this.selectionStart ? this.formatDateISO(this.selectionStart) : null;
    const to = this.selectionEnd ? this.formatDateISO(this.selectionEnd) : null;

    this.fromDate = from;
    this.toDate = to;
    this.fromDateChange.emit(from);
    this.toDateChange.emit(to);
    this.rangeChange.emit({ from, to });
  }

  clearSelection() {
    this.selectionStart = null;
    this.selectionEnd = null;
    this.fromDate = null;
    this.toDate = null;
    this.fromDateChange.emit(null);
    this.toDateChange.emit(null);
    this.rangeChange.emit({ from: null, to: null });
  }

  applyAndClose() {
    this.emitValues();
    this.isOpen = false;
  }
}
